import {
  Appointment,
  AppointmentSeriesSummary,
  AppointmentSignOffResult,
  CompleteAppointmentDto,
  CreateAppointmentDto,
  DeleteAppointmentResult,
  EndDepartureDto,
  FollowUpAppointmentDto,
  UpdateAppointmentDto,
  UpdateAppointmentSeriesResult,
  UpdateScheduleSeriesDto,
  UpdateTechnicianCommentDto, StartWorkItemDto } from "@myapp/contract";
import apiClient from "../../../shared/config/axios";

export const appointmentApi = {
  /**
   * One route for one-off and recurring alike: pass `recurrence: { frequency, until }` on the DTO
   * and the backend expands the rule, reserves every occurrence and mints the `seriesId`. The
   * response is always the FIRST occurrence — fetch the rest with `getSeries`.
   *
   * Replaces the old `bulkCreate`, which sent a client-generated `seriesId` and a pre-expanded
   * `plannedStarts[]`. That route is gone.
   */
  create: (data: CreateAppointmentDto) =>
    apiClient.post<Appointment>("/appointments", data),

  update: (id: number, dto: UpdateAppointmentDto) =>
    apiClient.patch<Appointment>(`/appointments/${id}`, dto),

  /**
   * `forFullSeries` also removes every LATER occurrence, skipping completed and started ones.
   * Replaces the old series `shorten` and `cancel-remaining` routes.
   */
  remove: (id: number, forFullSeries = false) =>
    apiClient.delete<DeleteAppointmentResult>(`/appointments/${id}`, {
      params: forFullSeries ? { forFullSeries: true } : undefined,
    }),

  /**
   * "Anfahrt starten" — the server stamps the raw departure instant. `body` only confirms starting
   * an item the office is rescheduling; a plain tap sends none.
   */
  startTravel: (id: number, body?: StartWorkItemDto) =>
    apiClient.post<Appointment>(`/appointments/${id}/travel/start`, body),

  /** "Arbeit starten" — the server stamps the instant. `body` as on `startTravel`. */
  start: (id: number, body?: StartWorkItemDto) =>
    apiClient.post<Appointment>(`/appointments/${id}/start`, body),

  /** `Rückgängig` — clears the last stamped instant so the next tap records an honest "now". */
  revertPhase: (id: number) =>
    apiClient.post<Appointment>(`/appointments/${id}/phase/revert`),

  /** "Abfahrt beenden" — closes the departure phase and the work session. */
  endDeparture: (id: number, dto: EndDepartureDto) =>
    apiClient.post<Appointment>(`/appointments/${id}/departure/end`, dto),

  complete: (id: number, dto: CompleteAppointmentDto) =>
    apiClient.patch<AppointmentSignOffResult>(`/appointments/${id}/complete`, dto),

  followUp: (id: number, dto: FollowUpAppointmentDto) =>
    apiClient.patch<AppointmentSignOffResult>(`/appointments/${id}/follow-up`, dto),

  getById: (id: number) =>
    apiClient.get<Appointment>(`/appointments/${id}`),

  getByWorkOrderId: (workOrderId: string) =>
    apiClient.get<Appointment[]>(`/appointments/by-work-order/${workOrderId}`),

  getSeries: (seriesId: string) =>
    apiClient.get<AppointmentSeriesSummary>(`/appointments/series/${seriesId}`),

  /**
   * Move the horizon. One call covers what used to be three routes: a later `until` grows the
   * series, an earlier one trims it. `frequency` is stored server-side and is not patchable.
   */
  updateSeries: (seriesId: string, dto: UpdateScheduleSeriesDto) =>
    apiClient.patch<UpdateAppointmentSeriesResult>(`/appointments/series/${seriesId}`, dto),

  detachFromSeries: (id: number) =>
    apiClient.patch<Appointment>(`/appointments/${id}/detach-from-series`),

  uploadFiles: (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return apiClient.post<string[]>("/appointments/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // ── Field feedback — the technician's comment and files, during and after the job ──

  /** `null` clears the comment. Assigned technician or office only (403 otherwise). */
  updateTechnicianComment: (id: number, dto: UpdateTechnicianCommentDto) =>
    apiClient.patch<Appointment>(`/appointments/${id}/technician-comment`, dto),

  /**
   * Unlike `uploadFiles`, this both stores the files and records them on the appointment with the
   * uploader — nothing has to be sent back on a later PATCH. Answers with the updated appointment.
   */
  uploadTechnicianAttachments: (id: number, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return apiClient.post<Appointment>(`/appointments/${id}/technician-attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /** Only the uploader or the office may remove a field attachment (403 otherwise). */
  removeTechnicianAttachment: (id: number, attachmentId: number) =>
    apiClient.delete<Appointment>(`/appointments/${id}/technician-attachments/${attachmentId}`),
};
